import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useMarketplace, useMasterData } from 'hooks';
import { PetType } from '@spike/model';
import { FixedPriceOption, DaycareDurationOption, LatePickupFeeType, DAYCARE_DURATION_OPTIONS } from 'model/Service';
import { LATE_PICKUP_FEE_OPTIONS, getDurationPriceUnit } from 'components/Daycare/Form/utils/DaycarePricingUtils';
import styles from './DaycareFixedPriceTable.module.scss';
import clsx from 'clsx';
import { Tooltip } from 'components/UI/V3/Tooltip/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { MarketplaceDepositType } from '@spike/marketplace-model';
import { AlertMessage } from 'components/UI/V3/AlertMessage';
import PriceField from 'components/UI/V3/Forms/PriceField';

// Helper function to create initial fixed price options
const createFixedPriceOptions = (petTypes: Array<PetType>): FixedPriceOption[] => {
    return petTypes.map(petType => ({
        petType,
        price: undefined
    }));
};

interface DaycareFixedPriceTableProps {
    selectedPetTypes: Array<PetType>;
    durationOption: DaycareDurationOption;
    fixedPrices: FixedPriceOption[];
    onFixedPricesChange: (prices: FixedPriceOption[]) => void;
    showLatePickupColumn?: boolean;
    latePickupFeeType?: LatePickupFeeType;
    latePickupFees?: Record<string, number>; // petTypeId -> late pickup fee amount
    onLatePickupFeesChange?: (fees: Record<string, number>) => void;
    className?: string;
    onValidationChange?: (isValid: boolean, errors: Record<string, string>) => void;
}

export const DaycareFixedPriceTable: React.FC<DaycareFixedPriceTableProps> = ({
    selectedPetTypes,
    durationOption,
    fixedPrices,
    onFixedPricesChange,
    showLatePickupColumn = false,
    latePickupFeeType,
    latePickupFees = {},
    onLatePickupFeesChange,
    className,
    onValidationChange
}) => {
    const marketplace = useMarketplace();
    const masterData = useMasterData();
    const deposit = marketplace.deposit;
    const depositType = deposit.type;

    // States for onBlur validation
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
    const [validatedFields, setValidatedFields] = useState<Set<string>>(new Set());

    // Filter available pet types based on selection
    const availablePetTypes = useMemo(() => {
        return masterData.petTypes.filter(petType => selectedPetTypes.some(selectedPt => selectedPt.id === petType.id));
    }, [selectedPetTypes, masterData.petTypes]);

    // Synchronize fixed prices with selected pet types
    useEffect(() => {
        if (availablePetTypes.length === 0) return;

        const existingPetTypeIds = new Set(fixedPrices.map(price => price.petType.id));

        const missingPetTypes = availablePetTypes.filter(petType => !existingPetTypeIds.has(petType.id));

        const currentPetTypeIds = new Set(availablePetTypes.map(pt => pt.id));
        const validPrices = fixedPrices.filter(price => currentPetTypeIds.has(price.petType.id));

        // Only update if there are changes
        if (missingPetTypes.length > 0 || validPrices.length !== fixedPrices.length) {
            const newPrices = missingPetTypes.map(petType => ({
                petType,
                price: undefined
            }));

            // Combine valid existing prices with new ones
            const updatedPrices = [...validPrices, ...newPrices];
            onFixedPricesChange(updatedPrices);
        }
    }, [availablePetTypes, fixedPrices, onFixedPricesChange]);

    const handlePriceChange = (petTypeId: string, value: number | undefined) => {
        const price = value;
        const updatedPrices = fixedPrices.map(priceOption => {
            if (priceOption.petType.id === petTypeId) {
                return { ...priceOption, price };
            }
            return priceOption;
        });
        onFixedPricesChange(updatedPrices);
    };

    const getPriceUnit = () => {
        return getDurationPriceUnit(durationOption);
    };

    const getCurrentPrice = (petTypeId: string): number | undefined => {
        const priceOption = fixedPrices.find(p => p.petType.id === petTypeId);
        return priceOption?.price !== undefined && priceOption?.price !== null && priceOption.price >= 0
            ? Number(priceOption.price.toFixed(2))
            : undefined;
    };

    const handleLatePickupFeeChange = (petTypeId: string, value: number | undefined) => {
        if (!onLatePickupFeesChange) return;

        const fee = value || 0;
        const updatedFees = {
            ...latePickupFees,
            [petTypeId]: fee
        };
        onLatePickupFeesChange(updatedFees);
    };

    const getCurrentLatePickupFee = (petTypeId: string): number | undefined => {
        const fee = latePickupFees[petTypeId];
        return fee && fee > 0 ? fee : undefined;
    };

    // Helper to validate if the price is lower than the deposit
    const isPriceLowerThanDeposit = (petTypeId: string): boolean => {
        if (depositType !== MarketplaceDepositType.Amount) {
            return false;
        }

        const currentPrice = getCurrentPrice(petTypeId);
        if (!currentPrice || currentPrice <= 0) {
            return false;
        }

        const depositAmount = typeof deposit.value === 'number' ? deposit.value : parseFloat(deposit.value || '0');
        return currentPrice < depositAmount;
    };

    // Handler for onBlur validation
    const handlePriceBlur = useCallback(
        (petTypeId: string) => {
            const newValidatedFields = new Set(validatedFields);
            newValidatedFields.add(petTypeId);
            setValidatedFields(newValidatedFields);

            // Validate deposit constraint
            const errors = { ...validationErrors };

            if (isPriceLowerThanDeposit(petTypeId)) {
                errors[petTypeId] = 'Price is lower than deposit amount';
            } else {
                delete errors[petTypeId];
            }

            setValidationErrors(errors);

            // Notify parent about validation state
            onValidationChange?.(Object.keys(errors).length === 0, errors);
        },
        [validatedFields, validationErrors, onValidationChange, isPriceLowerThanDeposit]
    );

    // Helper to show alert only after validation
    const shouldShowDepositAlert = (petTypeId: string): boolean => {
        return validatedFields.has(petTypeId) && isPriceLowerThanDeposit(petTypeId);
    };

    // Helper to get the header of the Late Pickup column
    const getLatePickupColumnHeader = (): string => {
        if (!latePickupFeeType) return 'Late Pickup Fee';
        return LATE_PICKUP_FEE_OPTIONS[latePickupFeeType]?.label || 'Late Pickup Fee';
    };

    // Helper to get the description of the Late Pickup Fee
    const getLatePickupDescription = (): string => {
        if (!latePickupFeeType) return '';
        return LATE_PICKUP_FEE_OPTIONS[latePickupFeeType]?.description || '';
    };

    return (
        <div className={`${styles.container} ${className || ''}`}>
            <div className={styles.header}>
                <div className={styles.headerRow}>
                    <span className={styles.typeColumn}>Type</span>
                    <span
                        className={clsx(
                            styles.priceColumn,
                            durationOption === DAYCARE_DURATION_OPTIONS.HOURLY && styles.hourlyPriceColumn
                        )}
                    >
                        Price <span className={styles.required}>*</span>
                    </span>
                    {showLatePickupColumn && (
                        <span className={styles.latePickupColumn}>
                            {getLatePickupColumnHeader()} <span className={styles.required}>*</span>
                            <Tooltip
                                content={getLatePickupDescription()}
                                placement="top"
                                className={styles.customTooltip}
                            >
                                <div>
                                    <FontAwesomeIcon
                                        icon={faInfoCircle}
                                        className={styles.tooltipIcon}
                                    />
                                </div>
                            </Tooltip>
                        </span>
                    )}
                </div>
            </div>

            <div className={styles.rows}>
                {availablePetTypes.map(petType => (
                    <React.Fragment key={petType.id}>
                        <div className={styles.row}>
                            <div className={styles.typeCell}>
                                <span className={styles.petTypeName}>{petType.plural}</span>
                            </div>
                            <div
                                className={clsx(
                                    styles.priceCell,
                                    durationOption === DAYCARE_DURATION_OPTIONS.HOURLY && styles.hourlyPriceColumn
                                )}
                            >
                                <div className={styles.priceInputContainer}>
                                    <PriceField
                                        scale={2}
                                        value={getCurrentPrice(petType.id)}
                                        onChange={(value: number | undefined) => handlePriceChange(petType.id, value)}
                                        onBlur={() => handlePriceBlur(petType.id)}
                                        placeholder="0.00"
                                        className={styles.priceInput}
                                    />
                                    <span
                                        className={clsx(
                                            styles.priceUnit,
                                            durationOption === DAYCARE_DURATION_OPTIONS.HOURLY && styles.showPriceUnit
                                        )}
                                    >
                                        {getPriceUnit()}
                                    </span>
                                </div>
                            </div>
                            {showLatePickupColumn && (
                                <div className={styles.latePickupCell}>
                                    <div className={styles.priceInputContainer}>
                                        <PriceField
                                            scale={2}
                                            value={getCurrentLatePickupFee(petType.id)}
                                            onChange={(value: number | undefined) =>
                                                handleLatePickupFeeChange(petType.id, value)
                                            }
                                            placeholder="0.00"
                                            className={styles.priceInput}
                                        />
                                        <span className={styles.priceUnit}>{getPriceUnit()}</span>
                                    </div>
                                </div>
                            )}
                            {/* Alert for deposit validation - Only show after field validation */}
                            {shouldShowDepositAlert(petType.id) && (
                                <AlertMessage
                                    icon={faInfoCircle}
                                    iconColor="#BAA997"
                                    bdColor="#BCB8AE"
                                    bgColor="#F8F5F1"
                                    className={styles.alert}
                                >
                                    <span>
                                        The price entered is lower than the price you have set for your deposits.
                                    </span>
                                </AlertMessage>
                            )}
                        </div>
                    </React.Fragment>
                ))}
            </div>

            {availablePetTypes.length === 0 && (
                <div className={styles.emptyState}>
                    <p>Please select pet types in Step 1 to configure pricing</p>
                </div>
            )}
        </div>
    );
};
